import type mapboxgl from 'mapbox-gl'

const DEFAULT_DRAW_COLOR = '#0059ff'

/**
 * Mapbox-gl-draw injects each style as `<style-id>.cold` and `<style-id>.hot`.
 * These are the IDs declared in MapDraw.vue together with the paint props that
 * carry the user-visible color.
 */
const COLOR_TARGETS: { id: string; props: string[] }[] = [
  { id: 'highlight-active-points', props: ['circle-color'] },
  { id: 'points-are-blue', props: ['circle-color'] },
  { id: 'gl-draw-line', props: ['line-color'] },
  { id: 'gl-draw-polygon-fill', props: ['fill-color', 'fill-outline-color'] },
  { id: 'gl-draw-polygon-stroke-active', props: ['line-color'] },
  { id: 'gl-draw-points-vertices', props: ['circle-color'] },
  { id: 'gl-draw-line-static', props: ['line-color'] },
  { id: 'gl-draw-polygon-fill-static', props: ['fill-color', 'fill-outline-color'] },
  { id: 'gl-draw-polygon-stroke-static', props: ['line-color'] }
]

function normalizeColor(color: string | null | undefined): string {
  if (!color) return DEFAULT_DRAW_COLOR
  return color.startsWith('#') ? color : `#${color}`
}

/**
 * Override the fallback color of every gl-draw paint expression so the
 * in-progress (no `user_color` property yet) preview matches the chosen
 * template. Per-feature `user_color` still wins for committed features.
 */
export function setDrawColor(
  map: mapboxgl.Map | null | undefined,
  color: string | null | undefined
): void {
  if (!map) return
  const resolved = normalizeColor(color)
  const expression = ['case', ['has', 'user_color'], ['get', 'user_color'], resolved] as unknown[]
  for (const { id, props } of COLOR_TARGETS) {
    for (const suffix of ['.cold', '.hot']) {
      const layerId = `${id}${suffix}`
      if (!map.getLayer(layerId)) continue
      for (const prop of props) {
        try {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          map.setPaintProperty(layerId, prop as Parameters<typeof map.setPaintProperty>[1], expression as any)
        } catch (e) {
          console.warn(`[MapDraw] Failed to set ${prop} on ${layerId}:`, e)
        }
      }
    }
  }
}

export function resetDrawColor(map: mapboxgl.Map | null | undefined): void {
  setDrawColor(map, DEFAULT_DRAW_COLOR)
}
